import { supabase } from '../lib/supabase';
import { documentoDoPagador, type PagadorUc } from '../lib/pagadorUc';
import { ucComCadastroTravado } from '../lib/permissaoCadastro';
// O motivo vai junto do update (ver src/services/cadastroUc.ts): o Faturamento vê no
// cálculo e na emissão do boleto.
import { gravarComMotivo } from './cadastroUc';

// Pagadores do boleto (migration 20261013180000). Regra pura em src/lib/pagadorUc.ts.

const CAMPOS = 'id, nome, cpf_cnpj, email, whatsapp, conferir_documento';

/** Pagador ligado à UC, ou null (UC sem pagador ou tabela ainda não criada). */
export async function buscarPagadorDaUc(ucId: number | string | null | undefined): Promise<PagadorUc | null> {
    if (ucId === null || ucId === undefined || ucId === '') return null;
    const { data, error } = await supabase
        .from('unidades_consumidoras')
        .select(`pagador:pagadores(${CAMPOS})`)
        .eq('id', ucId)
        .maybeSingle();
    // Antes da migration a coluna não existe: segue sem pagador, como era.
    if (error) return null;
    return ((data as any)?.pagador as PagadorUc) ?? null;
}

/** Quantas UCs usam este pagador — o aviso antes de editar. */
export async function contarUcsDoPagador(pagadorId: string): Promise<number> {
    const { count } = await supabase
        .from('unidades_consumidoras')
        .select('id', { count: 'exact', head: true })
        .eq('pagador_id', pagadorId);
    return count ?? 0;
}

/** Procura por nome ou CPF/CNPJ, para vincular a UC a um pagador que já existe. */
export async function procurarPagadores(termo: string): Promise<PagadorUc[]> {
    const t = termo.trim();
    if (t.length < 3) return [];
    const doc = t.replace(/\D/g, '');
    let q = supabase.from('pagadores').select(CAMPOS).limit(15);
    q = doc.length >= 3 && doc.length === t.replace(/[.\-/\s]/g, '').length
        ? q.like('cpf_cnpj', `%${doc}%`)
        : q.ilike('nome', `%${t}%`);
    const { data, error } = await q.order('nome');
    if (error) throw error;
    return (data ?? []) as PagadorUc[];
}

export interface DadosPagador {
    nome: string;
    cpf_cnpj: string;
    email?: string | null;
    whatsapp?: string | null;
}

function validar(d: DadosPagador): { nome: string; cpf_cnpj: string; email: string | null; whatsapp: string | null } {
    const nome = d.nome.trim();
    const cpf_cnpj = documentoDoPagador(d.cpf_cnpj);
    if (!nome) throw new Error('Informe o nome do pagador.');
    if (!cpf_cnpj) throw new Error('Informe um CPF (11 dígitos) ou CNPJ (14 dígitos) válido.');
    return {
        nome,
        cpf_cnpj,
        email: d.email?.trim() || null,
        whatsapp: d.whatsapp?.trim() || null,
    };
}

/** Edita o pagador — vale para TODAS as UCs ligadas a ele. */
export async function atualizarPagador(id: string, dados: DadosPagador, usuarioId?: string | null, motivo?: string | null): Promise<void> {
    const v = validar(dados);
    const { data, error } = await gravarComMotivo(
        p => supabase.from('pagadores').update(p).eq('id', id).select('id'),
        { ...v, conferir_documento: false, atualizado_por: usuarioId ?? null, atualizado_em: new Date().toISOString() },
        motivo,
    );
    if (error) {
        if (error.code === '23505') throw new Error('Já existe outro pagador com este CPF/CNPJ. Vincule a UC a ele em vez de repetir o documento.');
        throw error;
    }
    // A RLS que barra o UPDATE (#6239: só Financeiro ou adm) não dá erro, só zero linhas.
    if (!data?.length) throw new Error('Só o Financeiro ou um administrador altera o pagador. Peça a correção a eles.');
}

/**
 * O pagador com este CPF/CNPJ, criando se não existir. Um documento é um pagador
 * só (o Sicoob guarda um nome por documento): se já existe, vale o nome de lá, e
 * `nomeDiferente` diz se o digitado era outro — a tela avisa.
 */
export async function resolverPagador(dados: DadosPagador): Promise<{ pagador: PagadorUc; jaExistia: boolean; nomeDiferente: boolean }> {
    const v = validar(dados);
    const { data: existente, error: erroBusca } = await supabase
        .from('pagadores').select(CAMPOS).eq('cpf_cnpj', v.cpf_cnpj).eq('conferir_documento', false).maybeSingle();
    if (erroBusca) throw erroBusca;
    if (existente) {
        const p = existente as PagadorUc;
        const chave = (s: string) => s.normalize('NFD').replace(/\p{Diacritic}/gu, '').toUpperCase().replace(/\s+/g, ' ').trim();
        return { pagador: p, jaExistia: true, nomeDiferente: chave(p.nome) !== chave(v.nome) };
    }
    const { data, error } = await supabase.from('pagadores').insert(v).select(CAMPOS).single();
    if (error) throw error;
    return { pagador: data as PagadorUc, jaExistia: false, nomeDiferente: false };
}

/** Cria um pagador e liga a UC a ele. Se o documento já existe, só liga. */
export async function criarEVincularPagador(ucId: number | string, dados: DadosPagador, motivo?: string | null): Promise<PagadorUc> {
    const { pagador } = await resolverPagador(dados);
    await vincularPagador(ucId, pagador.id, motivo);
    return pagador;
}

/** Liga a UC a um pagador que já existe. */
export async function vincularPagador(ucId: number | string, pagadorId: string, motivo?: string | null): Promise<void> {
    const { error } = await gravarComMotivo(
        p => supabase.from('unidades_consumidoras').update(p).eq('id', ucId),
        { pagador_id: pagadorId },
        motivo,
    );
    if (error) throw error;
}

/**
 * Aplica a resposta "O assinante também será o pagador?" a uma negociação que já
 * existe ("Editar cliente"). Muda o pagador da negociação e o das UCs dela que
 * acompanham a escolha anterior — sem pagador, com o pagador anterior da
 * negociação, ou com a cópia do próprio cliente. UC com pagador personalizado
 * (o Fundo de uma Secretaria, por exemplo) não é tocada.
 *
 * #6239: sem a permissão "Editar dados cadastrais", UC já em operação não muda de
 * pagador (o banco barraria). Ela fica de fora e volta em `ucsPuladas`, para a
 * tela avisar que o Financeiro precisa fazer essa parte.
 */
export async function aplicarPagadorNaNegociacao(
    negociacaoId: number | string,
    assinantePaga: boolean,
    dados?: DadosPagador,
    opcoes?: { podeAlterarUcEmOperacao?: boolean },
): Promise<{ ucsAtualizadas: number; ucsPuladas: number; nomeDiferente: boolean; nomeCadastrado: string | null }> {
    const podeEmOperacao = opcoes?.podeAlterarUcEmOperacao ?? true;
    const { data: neg, error: erroNeg } = await supabase
        .from('negociacoes').select('pagador_id').eq('id', negociacaoId).maybeSingle();
    if (erroNeg) throw erroNeg;
    const anterior: string | null = (neg as any)?.pagador_id ?? null;

    const { data: todasUcs, error: erroUcs } = await supabase
        .from('unidades_consumidoras')
        .select('id, nome_cliente, cpf_cnpj_cliente, pagador_id, cadastro_aprovado, situacao, pagador:pagadores(cpf_cnpj)')
        .eq('negociacao_id', negociacaoId);
    if (erroUcs) throw erroUcs;
    const copiaDoCliente = (u: any) => !!u.pagador && u.pagador.cpf_cnpj === documentoDoPagador(u.cpf_cnpj_cliente);
    const acompanha = (u: any) => !u.pagador_id || (anterior && u.pagador_id === anterior) || copiaDoCliente(u);
    const liberada = (u: any) => podeEmOperacao || !ucComCadastroTravado(u);
    const ucs = (todasUcs ?? []).filter(liberada);
    const ucsPuladas = (todasUcs ?? []).filter((u: any) => !liberada(u) && acompanha(u)).length;

    let ucsAtualizadas = 0;
    let nomeDiferente = false;
    let nomeCadastrado: string | null = null;
    // Contexto para o Faturamento ver no cálculo/emissão de UC em operação.
    const motivo = `Pagador definido em "Editar cliente" da negociação #${negociacaoId}.`;

    if (!assinantePaga) {
        if (!dados) throw new Error('Informe o pagador.');
        const r = await resolverPagador(dados);
        nomeDiferente = r.nomeDiferente;
        nomeCadastrado = r.pagador.nome;
        const { error } = await supabase.from('negociacoes')
            .update({ pagador_id: r.pagador.id, pagador_e_assinante: false }).eq('id', negociacaoId);
        if (error) throw error;
        const alvo = (ucs ?? []).filter(acompanha).filter((u: any) => u.pagador_id !== r.pagador.id).map((u: any) => u.id);
        if (alvo.length) {
            const { error: e2 } = await gravarComMotivo(
                p => supabase.from('unidades_consumidoras').update(p).in('id', alvo),
                { pagador_id: r.pagador.id },
                motivo,
            );
            if (e2) throw e2;
            ucsAtualizadas = alvo.length;
        }
    } else {
        const { error } = await supabase.from('negociacoes')
            .update({ pagador_id: null, pagador_e_assinante: true }).eq('id', negociacaoId);
        if (error) throw error;
        // Quem estava com o pagador anterior da negociação volta para o próprio cliente.
        for (const u of (ucs ?? []) as any[]) {
            if (!anterior || u.pagador_id !== anterior) continue;
            if (!documentoDoPagador(u.cpf_cnpj_cliente) || !(u.nome_cliente || '').trim()) continue;
            const r = await resolverPagador({ nome: u.nome_cliente, cpf_cnpj: u.cpf_cnpj_cliente });
            await vincularPagador(u.id, r.pagador.id, motivo);
            ucsAtualizadas += 1;
        }
    }
    return { ucsAtualizadas, ucsPuladas, nomeDiferente, nomeCadastrado };
}
